/*
 * Todo:
 * - After BE is merged in, make sure all api end points, data structures from FE are connected to BE implementation.
 */
import axios from 'axios';
import { PolicyErrorCodesEnum } from '@general/js/enums';
import { DHLError, getCurrentDate, handleHttpResponse, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError } from '@general/js/utils';
import { EnvironmentVariables } from '@general/js/environmentVariables';
import AbstractClass from './PaymentDetailsAbstractServices';
import { Services } from '../Services';

export default class PaymentDetailsServices extends AbstractClass {
  /**
   * Get ref data for payment selection page
   * @returns {object} Object containing reference data information text
   */
  static async getPaymentDetailsSelectionRefData() {
    try {
      const response = await axios.get(Services.PAYMENT_DETAILS.GET_PAYMENT_DETAILS_SELECTION_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetailsPageReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Retrieves reference data for view/edit account details screens from CMS BE
   * @returns Ref data object
   */
  static async getPaymentDetailsPageReferenceData() {
    try {
      const response = await axios.get(Services.PAYMENT_DETAILS.GET_PAYMENT_DETAILS_REFERENCE_DATA);

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetailsPageReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get payment details data.
   * @param {string} accessingPersonCode person code of member payments being accessed
   * @param {string} memberNumber membership id (policy number)
   * @returns {object}
   */
  static async getPaymentDetails(memberNumber, personCode, currentDate = getCurrentDate()) {
    try {
      const response = await axios.get(Services.PAYMENT_DETAILS.GET_PAYMENT_DETAILS(memberNumber, personCode, currentDate));

      if (handleHttpResponse(response)) {
        return JSON.parse(JSON.stringify(response.data));
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMemberDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Update Payment details data
   * @param {object} paymentDetails object of payment details containing premium payment account and claims account
   * @param {string} memberNumber membership id (policy number)
   * @param {string} accessingPersonCode person code of member payments is being changed for
   * @returns {object}
   */
  static async postPaymentDetails(paymentDetails, memberNumber, accessingPersonCode) {
    try {
      const response = await axios.patch(
        Services.PAYMENT_DETAILS.PATCH_PAYMENT_DETAILS(memberNumber, accessingPersonCode),
        {
          ...paymentDetails,
          currentDate: getCurrentDate(),
        },
        { timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT) },
      );
      return handleHttpResponse(response);
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_ERROR) {
        throw new SubmitPolicyError(`postPaymentDetails() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      } else if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new SubmitPolicyTimeoutError(`postPaymentDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.SUBMIT_POLICY_IN_PROCESS_ERROR) {
        throw new SubmitPolicyInProcessError(`postPaymentDetails() - call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`postPaymentDetails() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get reference data for no impact page
   * @returns {object}
   */
  static async getPremiumImpactPageReferenceData() {
    try {
      const response = await axios.get(Services.PAYMENT_DETAILS.GET_PREMIUM_IMPACT_REF_DATA);

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getPremiumImpactPageReferenceData() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
